import { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HistoryIcon from "@mui/icons-material/History";
import { formatCurrency } from "../../../../../utils/mockWalletData";
import AppService from "../../../../../services/AppService";

/**
 * CustomerTopupHistory Tab - Shows completed customer top-up transactions
 * Phase 8: With Transaction Detail Modal and Audit Trail
 */

function getPaymentMethodIcon(method) {
  if (method === 'ONLINE') {
    return {
      icon: CreditCardIcon,
      label: 'Online',
      color: 'success',
      tooltip: 'Online Payment (Credit Card / Gateway)'
    };
  } else {
    return {
      icon: AccountBalanceIcon,
      label: 'Offline',
      color: 'primary',
      tooltip: 'Offline Payment (Bank Transfer / Check)'
    };
  }
}

function getStatusColor(status) {
  switch (status) {
    case "COMPLETED":
    case "CONFIRMED":
      return "success";
    case "PENDING":
      return "warning";
    case "FAILED":
      return "error";
    default:
      return "default";
  }
}

// Transaction Detail Modal Component
function TransactionDetailModal({ open, onClose, transaction }) {
  const [activeTab, setActiveTab] = useState(0);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Load audit trail when modal opens and Audit tab is selected
  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);

    if (newValue === 1 && auditTrail.length === 0 && transaction?.transactionId) {
      // Load audit trail
      setLoadingAudit(true);
      try {
        const response = await AppService.get(`/v1/transactions/${transaction.transactionId}/audit`);
        console.log('[CustomerTopupHistory] Audit response:', response);
        // AppService interceptor already unwraps response.data, so response is the data object
        if (response && response.success && response.data) {
          console.log('[CustomerTopupHistory] Setting audit trail:', response.data);
          setAuditTrail(response.data);
        }
      } catch (error) {
        console.error('Error loading audit trail:', error);
      } finally {
        setLoadingAudit(false);
      }
    }
  };

  if (!transaction) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontSize: '0.95rem', fontWeight: 'bold' }}>
            Transaction Details
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
            {transaction.referenceNumber || transaction.id}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={transaction.status}
            size="small"
            color={getStatusColor(transaction.status)}
            sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
          />
          <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            '& .MuiTab-root': {
              fontSize: '0.8125rem',
              minHeight: '42px'
            }
          }}
        >
          <Tab label="Overview" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HistoryIcon sx={{ fontSize: '1rem' }} />
                <span style={{ fontSize: '0.8125rem' }}>Audit Trail</span>
                {auditTrail.length > 0 && (
                  <Chip label={auditTrail.length} size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }} />
                )}
              </Box>
            }
          />
        </Tabs>

        {/* Overview Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  From
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '0.875rem' }}>
                  {transaction.from?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  ({transaction.from?.type})
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  To
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '0.875rem' }}>
                  {transaction.to?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  ({transaction.to?.type})
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Amount
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '1.1rem' }}>
                  {formatCurrency(transaction.amount)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Date
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '0.875rem' }}>
                  {new Date(transaction.date).toLocaleString()}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Payment Method
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {(() => {
                    const paymentInfo = getPaymentMethodIcon(transaction.paymentMethod);
                    const IconComponent = paymentInfo.icon;
                    return (
                      <>
                        <IconComponent color={paymentInfo.color} sx={{ fontSize: '1.1rem' }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          {paymentInfo.label}
                        </Typography>
                      </>
                    );
                  })()}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Transaction ID
                </Typography>
                <Typography variant="body2" sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  mt: 0.5,
                  wordBreak: 'break-all'
                }}>
                  {transaction.id}
                </Typography>
              </Grid>

              {transaction.referenceNumber && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Reference Number
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5, fontSize: '0.875rem' }}>
                    {transaction.referenceNumber}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8125rem' }}>
                  {transaction.notes || "No notes available"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Audit Trail Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 2.5 }}>
            {loadingAudit ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : auditTrail.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
                No audit trail records found for this transaction.
              </Alert>
            ) : (
              <>
                {/* Summary Header */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.5 }}>
                    Transaction Change History
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {auditTrail.length} change{auditTrail.length !== 1 ? 's' : ''} recorded • Most recent first
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  {auditTrail.map((audit, index) => (
                  <Paper
                    key={audit.id || index}
                    elevation={2}
                    sx={{
                      p: 1.5,
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.8125rem' }}>
                        {audit.fieldLabel || audit.fieldName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {new Date(audit.changedAt).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', my: 1 }}>
                      <Chip
                        label={audit.oldValue || 'N/A'}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: '45%', fontSize: '0.7rem', height: 22 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>→</Typography>
                      <Chip
                        label={audit.newValue || 'N/A'}
                        size="small"
                        color="primary"
                        sx={{ maxWidth: '45%', fontSize: '0.7rem', height: 22 }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Changed by:
                      </Typography>
                      <Chip
                        label={audit.changedByUsername || 'System'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                      {audit.changeSource && (
                        <Chip
                          label={audit.changeSource}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>

                    {audit.changeReason && (
                      <>
                        <Divider sx={{ my: 0.8 }} />
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.75rem' }}>
                          <strong>Reason:</strong> {audit.changeReason}
                        </Typography>
                      </>
                    )}
                  </Paper>
                ))}
                </Stack>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

const CustomerTopupHistory = ({ data }) => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleRowClick = (transaction) => {
    setSelectedTransaction(transaction);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
  };

  if (!data || data.items.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          No customer top-up transactions found.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "primary.main" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", color: "white", fontSize: '0.8125rem', py: 1.2 }}>Transaction #</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white", fontSize: '0.8125rem', py: 1.2 }}>From</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "white", fontSize: '0.8125rem', py: 1.2 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white", fontSize: '0.8125rem', py: 1.2 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white", fontSize: '0.8125rem', py: 1.2 }}>Payment Method</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white", fontSize: '0.8125rem', py: 1.2 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((transaction) => (
              <TableRow
                key={transaction.id}
                onClick={() => handleRowClick(transaction)}
                sx={{
                  "&:hover": {
                    bgcolor: "grey.100",
                    cursor: 'pointer'
                  },
                  transition: 'background-color 0.2s'
                }}
              >
                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                    {transaction.referenceNumber || transaction.id.substring(0, 8)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontWeight: "medium", fontSize: '0.8125rem', py: 1 }}>
                  {transaction.from?.name}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", fontSize: '0.8125rem', py: 1 }}>
                  {formatCurrency(transaction.amount)}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    {new Date(transaction.date).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  {(() => {
                    const paymentInfo = getPaymentMethodIcon(transaction.paymentMethod);
                    const IconComponent = paymentInfo.icon;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconComponent
                          sx={{ fontSize: '1rem', color: `${paymentInfo.color}.main` }}
                          titleAccess={paymentInfo.tooltip}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.8125rem' }}>
                          {paymentInfo.label}
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip
                    label={transaction.status}
                    size="small"
                    color={getStatusColor(transaction.status)}
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Transaction Detail Modal */}
      <TransactionDetailModal
        open={modalOpen}
        onClose={handleCloseModal}
        transaction={selectedTransaction}
      />
    </Box>
  );
};

export default CustomerTopupHistory;
